import { isEmpty, isNil } from 'lodash';
import { useCallback } from 'react';
import { UrlActionButtons } from '@shared/components/UrlActionButtons';
import { View } from '@shared/components/View';
import { Link } from '@shared/components/link';
import { MBTextInput } from '@shared/components/textInput';
import { TruncateLink } from '@shared/components/truncate';
import { UrlField } from '@shared/data/entity/fields/UrlField';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { getComponentPlaceholder } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { addPrefixIfNeeded, validateUrl } from '@shared/util/validate/url';
import { EditFieldContainer } from '../EditFieldContainer';
import { useFieldDraft } from '../useFieldDraft';
import { UrlEditFieldProps } from './UrlEditFieldProps';
import classNames from './UrlEditFieldDesktop.module.css';

export function UrlEditFieldDesktop({
  field,
  component,
  updateField,
  error,
  allowClear,
  style,
}: UrlEditFieldProps) {
  const fieldUrl = field?.url ?? '';
  const [draft, commitDraft] = useFieldDraft<string, string>(fieldUrl);
  const value = draft ?? fieldUrl;
  const [isFocused, onFocus, onBlur] = useBoolean(false);
  const isInvalid = !validateUrl(value);
  const hasValue = !isEmpty(value);
  const isReadOnly = !!component.readonly;
  const showClearButton = hasValue && !isReadOnly && allowClear;
  // Surface URL *formatting* errors on blur, independent of the form-level `error` prop
  // (which only includes local validation after a submit attempt). Required-field errors
  // still come solely from `error` and remain submit-gated.
  const formatError =
    hasValue && isInvalid ? i18n('workflowTemplate.component.error.url.invalid') : undefined;
  const displayError = error ?? formatError;
  const showError = !isFocused && !isEmpty(displayError);

  const updateValue = useCallback(
    (updatedValue: string) => {
      const url = updatedValue.trim();
      commitDraft(updatedValue, url);
      updateField(component.id, !isEmpty(url) ? new UrlField([{ url }]) : null);
    },
    [updateField, component.id, commitDraft],
  );

  const onClear = useCallback(() => {
    updateValue('');
  }, [updateValue]);

  return (
    <EditFieldContainer
      component={component}
      showLabel={true}
      showClearButton={showClearButton}
      onClear={onClear}
      error={showError ? displayError : undefined}
      style={style}
      printContent={field?.url || undefined}
    >
      <View row centerVertically className={classNames.urlEditFieldDesktop}>
        {isReadOnly && !isNil(field) && <ReadOnlyContent field={field} />}
        {!isReadOnly && (
          <EditableContent
            value={value}
            updateValue={updateValue}
            hasValue={hasValue}
            isInvalid={isInvalid}
            isFocused={isFocused}
            onFocus={onFocus}
            onBlur={onBlur}
            component={component}
            allowClear={allowClear}
          />
        )}
      </View>
    </EditFieldContainer>
  );
}

function EditableContent({
  value,
  updateValue,
  hasValue,
  isInvalid,
  isFocused,
  onFocus,
  onBlur,
  component,
  allowClear,
}: {
  value: string;
  updateValue: (value: string) => void;
  hasValue: boolean;
  isInvalid: boolean;
  isFocused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  component: API.AnyComponent;
  allowClear?: boolean;
}) {
  const { setComponentFocused, setComponentBlurred } = useComponentFocus();
  const showActionButtons = !isFocused && !isInvalid;

  const handleFocus = useCallback(() => {
    setComponentFocused();
    onFocus();
  }, [setComponentFocused, onFocus]);

  const handleBlur = useCallback(() => {
    const prefixedValue = addPrefixIfNeeded(value);
    if (prefixedValue !== value) {
      updateValue(prefixedValue);
    }
    setComponentBlurred();
    onBlur();
  }, [setComponentBlurred, onBlur, updateValue, value]);

  const inputStyle = {
    paddingVertical: hasValue ? 0 : grid(1.25),
  };

  return (
    <>
      <View style={{ flexGrow: 1 }}>
        <MBTextInput
          noBorder
          noPadding
          inputMode="url"
          textContentType="URL"
          keyboardType="url"
          value={value}
          onChangeText={updateValue}
          placeholder={getComponentPlaceholder(component)}
          onBlur={handleBlur}
          onFocus={handleFocus}
          autoCapitalize="none"
          style={inputStyle}
        />
      </View>
      {showActionButtons && (
        <View className={[classNames.actionButtons, allowClear && classNames.clearable]}>
          <UrlActionButtons url={value} />
        </View>
      )}
    </>
  );
}

function ReadOnlyContent({ field }: { field: UrlField }) {
  return (
    <View row centerVertically fullWidth>
      {/* On the View because Link doesn't pass extra props to its anchor. */}
      <View centerVertically style={{ flexShrink: 1 }} tooltip={field.url}>
        <Link href={field.url}>
          <TruncateLink href={field.url} />
        </Link>
      </View>
      <View paddingLeft={1}>
        <UrlActionButtons url={field.url} showOpen={false} />
      </View>
    </View>
  );
}
